import React, { FC, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { CreateWebhookSubscriptionInput, WebhookEntity } from 'models/webhook-subscription';

type CreateWebhookSubscriptionDialogProps = {
    open: boolean;
    organizationId: string;
    organizationName: string;
    onCancel: () => void;
    onSubmit: (input: CreateWebhookSubscriptionInput) => void;
};

// Shape check only. The backend decides which URLs are allowed (https on a
// public host in deployed environments, anything local in development,
// DD-3576) and the parent shows its rejection.
const isValidHttpUrl = (value: string): boolean => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
        return false;
    }
};

const ENTITY_OPTIONS: WebhookEntity[] = [WebhookEntity.ORDER];

const CreateWebhookSubscriptionDialog: FC<CreateWebhookSubscriptionDialogProps> = ({
    open,
    organizationId,
    organizationName,
    onCancel,
    onSubmit,
}: CreateWebhookSubscriptionDialogProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [entities, setEntities] = useState<WebhookEntity[]>([WebhookEntity.ORDER]);

    const resetAndClose = () => {
        setName('');
        setUrl('');
        setEntities([WebhookEntity.ORDER]);
        onCancel();
    };

    const toggleEntity = (entity: WebhookEntity, checked: boolean) => {
        setEntities((current) => (checked ? [...current, entity] : current.filter((e) => e !== entity)));
    };

    const trimmedUrl = url.trim();
    const urlLooksValid = trimmedUrl.length === 0 || isValidHttpUrl(trimmedUrl);
    const canSubmit = name.trim().length > 0 && trimmedUrl.length > 0 && urlLooksValid && entities.length > 0;

    const handleSubmit = () => {
        onSubmit({
            organization: organizationId,
            name: name.trim(),
            url: trimmedUrl,
            entities,
        });
        resetAndClose();
    };

    return (
        <DHDialog
            open={open}
            onClose={resetAndClose}
            maxWidth="sm"
            fullWidth
            title={translate('app-settings:CREATE_WEBHOOK')}
            actions={
                <Box display="flex" gap={1}>
                    <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                        {translate('app-settings:CREATE_WEBHOOK')}
                    </Button>
                </Box>
            }
        >
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    {translate('app-settings:CREATE_WEBHOOK_FOR_LAB', {
                        lab: organizationName,
                    })}
                </Typography>
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    label={translate('app-settings:WEBHOOK_NAME')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    label={translate('app-settings:WEBHOOK_URL')}
                    placeholder="https://example.com/webhooks/denthub"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    error={trimmedUrl.length > 0 && !urlLooksValid}
                    helperText={
                        trimmedUrl.length > 0 && !urlLooksValid
                            ? translate('app-settings:WEBHOOK_URL_INVALID')
                            : translate('app-settings:WEBHOOK_URL_HELP')
                    }
                />
            </Box>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:WEBHOOK_ENTITIES')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {translate('app-settings:WEBHOOK_ENTITIES_HELP')}
                </Typography>
                <FormGroup>
                    {ENTITY_OPTIONS.map((entity) => (
                        <FormControlLabel
                            key={entity}
                            control={
                                <Checkbox
                                    checked={entities.includes(entity)}
                                    onChange={(_, checked) => toggleEntity(entity, checked)}
                                />
                            }
                            label={translate(`app-settings:WEBHOOK_ENTITY_${entity}`)}
                        />
                    ))}
                </FormGroup>
            </Box>
        </DHDialog>
    );
};

export default CreateWebhookSubscriptionDialog;
